import React, { useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector, useDispatch } from 'react-redux';
import { Route, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, ButtonSize, PagePanel } from 'edenred-ui';

import { OrderType } from '@epi-constants/orderType';
import { getCompanyInvoicePFD } from '@epi-selectors/companyAccounts';
import { goToPage } from '@epi-actions/navigation';
import { useGetMetadataQuery } from '@epi-api/companyMetadata';
import { IOrderStatus } from '@epi-models/containers/OrderStatus';
import { useOnboardingWizard } from '@epi-shared/hooks';

import { getApiPaymentStatus } from '../../selectors/api';
import { routerPaths } from '../../constants/routerPaths';
import { PaymentStatuses } from '../../constants/paymentStatuses';
import { PaymentMethods } from '../../constants/paymentMethods';
import { NotFound } from '../NotFound/NotFound';
import { LoadingContainer } from '../../components/LoadingContainer/LoadingContainer';
import * as apiActions from '../../actions/api';
import * as navigationActions from '../../actions/navigation';

import './OrderStatus.scss';
import { CardOrderSuccessfulScreen } from './CardOrderSuccessfulScreen';
import { PDFInvoiceSuccess } from './PDFInvoiceSuccess';

function OrderStatus({
  paymentOnlyFlow = false,
  orderType: propsOrderType = '',
  isDeposit = false,
  apiPaymentStatus,
  getPaymentStatus
}: IOrderStatus) {
  const {
    status,
    paymentMethod,
    isLoading,
    isUnauthorized,
    orderType: apiOrderType
  } = apiPaymentStatus;
  const { t } = useTranslation();
  const { paymentId } = useParams<{ paymentId: string }>();
  const dispatch = useDispatch();
  const orderType = apiOrderType || propsOrderType;
  const isPrintInvoicePdfForTransferMoney = useSelector(getCompanyInvoicePFD);
  const { data: companyMetadata, isSuccess } = useGetMetadataQuery();

  useEffect(() => {
    if (
      orderType === OrderType.voucher &&
      isSuccess &&
      !companyMetadata?.orderVouchersEnabled
    ) {
      dispatch(goToPage(routerPaths.home));
    }
  }, [orderType, isSuccess, companyMetadata]);

  const {
    isOnBoardingActive,
    refetchFlags,
    isLoading: isLoadingFlags
  } = useOnboardingWizard();

  useEffect(() => {
    getPaymentStatus(paymentId);
  }, []);

  useEffect(() => {
    if (!isLoading) {
      refetchFlags();
    }
  }, [isLoading]);

  const redirectionUrl = () => {
    if (orderType === OrderType.digital) {
      return isDeposit
        ? routerPaths.loadCompanyAccount
        : routerPaths.cardOrderPayment;
    }
    return routerPaths.voucherOrderPayment;
  };

  function getTranslationPath(value, paid = false) {
    let after = value ? `.${value}` : '';
    if (orderType === OrderType.digital) {
      const digiOrderType = isDeposit ? 'deposit_order' : 'card_order';
      after = paid && !isDeposit ? `${after}_payment` : after;
      return `containers.order_status.${orderType}.${digiOrderType}${after}`;
    }
    return `containers.order_status.${orderType}${after}`;
  }

  const isPaidOnline = [
    PaymentMethods.CreditCard,
    PaymentMethods.Online
  ].includes(paymentMethod);

  const isPaymentSuccess =
    isPaidOnline &&
    [
      PaymentStatuses.Pending,
      PaymentStatuses.Paid,
      PaymentStatuses.Confirmed
    ].includes(status);

  const isSuccessful = !isPaidOnline || isPaymentSuccess;

  const handleClickGoToPage = (url: string) => {
    dispatch(
      goToPage(url, {
        params: { paymentId }
      })
    );
  };

  const onboardingFooter =
    isOnBoardingActive && propsOrderType !== OrderType.voucher
      ? {
          backText: t('components.onboarding.buttons.back'),
          onBack: () => {
            dispatch(goToPage(routerPaths.loadCompanyAccount));
          },

          submitText: t('components.onboarding.buttons.next'),
          onSubmit: () => {
            dispatch(goToPage(routerPaths.onboardingFinished));
          }
        }
      : undefined;

  const successTemplate = () => {
    if (isPrintInvoicePdfForTransferMoney) {
      return (
        <PDFInvoiceSuccess paymentId={paymentId} footer={onboardingFooter} />
      );
    }

    if (orderType === OrderType.digital) {
      return (
        <CardOrderSuccessfulScreen
          isPaymentSuccess={isPaymentSuccess}
          getTranslationPath={getTranslationPath}
          paymentMethod={paymentMethod}
          goToPage={handleClickGoToPage}
          footer={onboardingFooter}
        />
      );
    }
  };

  const canceledTemplate = () => {
    const buttonText = orderType === OrderType.digital ? 'digital' : 'voucher';
    return (
      <PagePanel
        title={t(getTranslationPath('title_failed'))}
        footer={
          onboardingFooter
            ? { ...onboardingFooter, disableSubmit: true }
            : undefined
        }
      >
        <h2 className="small">{t(getTranslationPath('body_failed'))}</h2>
        {paymentOnlyFlow ? (
          ''
        ) : (
          <div>
            <h2 className="small">
              {t('containers.order_status.new_customer_failed')}
            </h2>
          </div>
        )}
        <Button
          id="back-to-payment-method-btn"
          size={ButtonSize.Medium}
          className="float-right"
          onClick={() => handleClickGoToPage(redirectionUrl())}
        >
          {t(`containers.order_status.button_failed_${buttonText}`)}
        </Button>
      </PagePanel>
    );
  };

  return (
    <div className="OrderStatus">
      {isUnauthorized ? (
        <Route component={NotFound} />
      ) : (
        <div>
          <LoadingContainer isLoading={isLoading && !isLoadingFlags}>
            {isSuccessful && successTemplate()}
            {!isSuccessful && canceledTemplate()}
          </LoadingContainer>
        </div>
      )}
    </div>
  );
}

const mapStateToProps = state => ({
  apiPaymentStatus: getApiPaymentStatus(state)
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...apiActions, ...navigationActions }, dispatch);

const ConnectedOrderStatus = connect(
  mapStateToProps,
  mapDispatchToProps
)(OrderStatus);

export { ConnectedOrderStatus as OrderStatus };
